import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import {
  MAX_PACKAGE_NAME_LENGTH,
  PACKAGE_ROUTE,
  PURL_SAFE_HELP_MESSAGE,
} from "Constants";
import { useProjectsRoute } from "Hooks";
import { useUpdatePackageMutation } from "Services";
import { PackageSummary } from "Types";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useCreatePackageMutation } from "../../services/projects.api";
import { CreatePackageRequest, CreatePackageResponse } from "../../types";

type CreatePackageFormData = Omit<CreatePackageRequest["body"], "project_id">;

const createPackageSchema: yup.SchemaOf<CreatePackageFormData> = yup.object({
  name: yup
    .string()
    .required("Package name is required")
    .noInvisibleString("Package name should not be blank")
    .purlSafe()
    .max(
      MAX_PACKAGE_NAME_LENGTH,
      `Package name must be at most ${MAX_PACKAGE_NAME_LENGTH} characters`,
    ),
  description: yup
    .string()
    .optional()
    .max(500, "Max description length is 500 characters"),
  type: yup
    .mixed<CreatePackageFormData["type"]>()
    .oneOf(["ci-cd", "release"])
    .required("Package type is required"),
});

type CreatePackageProps = {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  packageInfo?: Omit<PackageSummary, "analysis_overview">;
  packageType?: "ci-cd" | "release";
  onClose: () => void;
};

const CreatePackageContent = ({
  projectId,
  projectName,
  packageType = "release",
  packageInfo,
  onClose,
}: Omit<CreatePackageProps, "isOpen">): React.JSX.Element => {
  const navigate = useNavigate();
  const projectsRoute = useProjectsRoute(projectId);
  const [createPackage] = useCreatePackageMutation();
  const [updatePackage] = useUpdatePackageMutation();

  const methods = useForm<CreatePackageFormData>({
    resolver: yupResolver(createPackageSchema),
    defaultValues: {
      description: packageInfo?.description || "",
      name: packageInfo?.name || "",
      type: "release",
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<CreatePackageFormData> = async (data) => {
    try {
      let result: CreatePackageResponse | null = null;

      if (packageInfo) {
        if (!isDirty) {
          onClose();
          return;
        }
        await updatePackage({
          body: data,
          packageId: packageInfo.package_id,
        }).unwrap();
      } else {
        result = await createPackage({
          body: {
            ...data,
            project_id: projectId ?? "",
          },
        }).unwrap();
      }

      Toast.show({
        title: `Package ${packageInfo ? "edited" : "created"}`,
        type: "success",
      });

      if (result?.package_id) {
        navigate(`${projectsRoute}/${PACKAGE_ROUTE}/${result.package_id}`);
      }
    } catch (error) {
      apiErrorToast(
        error,
        `Could not ${packageInfo ? "edit" : "create"} package`,
      );
    }

    onClose();
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          {packageInfo ? "Edit" : "Create"}{" "}
          {packageType === "release" ? "Release" : "CI/CD"} Package
        </Dialog.Header.Title>
        <Dialog.Header.Subtitle>
          Project: <em>{projectName}</em>
        </Dialog.Header.Subtitle>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                {packageInfo && (
                  <Dialog.Banner type="warning" title="Report URL change">
                    Renaming your package will also change the report URL for
                    all versions inside it. If you shared the old link, it will
                    no longer work.
                  </Dialog.Banner>
                )}
                <Form.TextField
                  name="name"
                  label="Package name"
                  id="package-name"
                  isRequired
                  errors={errors}
                  helperText={PURL_SAFE_HELP_MESSAGE}
                />
                <Form.TextArea
                  name="description"
                  label="Short description"
                  id="package-description"
                  errors={errors}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button id="cancel" color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                id={packageInfo ? "edit" : "create"}
                type="submit"
                color="primary"
              >
                {packageInfo ? "Edit" : "Create"}
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const CreatePackage = ({
  isOpen,
  ...props
}: CreatePackageProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <CreatePackageContent {...props} />
  </Dialog.Frame>
);

export default CreatePackage;
